/*
 * Vendor Globals — Exposes third-party libraries as window globals.
 *
 * This file MUST be imported as a module (not inline code) so esbuild
 * processes it during the import chain, BEFORE application modules that
 * reference these globals. If these assignments were inline code in
 * app_bundle.js, esbuild would place them AFTER all imports, and any
 * crash in the import chain would prevent them from executing.
 */

// Modernizr shim — Foundation 5 requires Modernizr for feature detection
// (touch events, CSS transitions, media queries). The full Modernizr was
// removed from vendor/ as obsolete, but Foundation 5 npm still references it.
// This minimal shim provides just enough for Foundation 5 to initialize.
if (typeof window.Modernizr === 'undefined') {
    window.Modernizr = {
        touch: ('ontouchstart' in window) || (navigator.maxTouchPoints > 0),
        csstransitions: true,
        csstransforms: true,
        csstransforms3d: true,
        mq: function (q) { return window.matchMedia(q).matches; }
    };
}

// Foundation is initialized by foundation_init.js after protenders.js loads.
// Do NOT call $(document).foundation() anywhere else — it runs after DOM ready
// as a separate script, ensuring Foundation always has jQuery available.
import $ from 'jquery';

// AG Grid Enterprise
import * as agGrid from 'ag-grid-enterprise';
window.agGrid = agGrid;

// Algolia Search
import * as algoliasearchModule from 'algoliasearch/lite';
import instantsearch from 'instantsearch.js';

// Import widgets from instantsearch.js v4 (ESM named exports)
// The v4 ES build defines non-configurable getter traps on instantsearch.widgets
// and instantsearch.connectors that throw ReferenceErrors. Bypass by importing
// from subpaths — same pattern as search_modules.js.
import {
    clearRefinements,
    configure,
    currentRefinements,
    hits,
    infiniteHits,
    menu,
    pagination,
    rangeInput,
    rangeSlider,
    refinementList,
    searchBox,
    sortBy,
    stats,
    panel,
    hierarchicalMenu,
    toggleRefinement
} from 'instantsearch.js/es/widgets';

import {
    connectCurrentRefinements,
    connectHits,
    connectRange,
    connectStats
} from 'instantsearch.js/es/connectors';

import historyRouter from 'instantsearch.js/es/lib/routers/history';

// Create wrapper with clean widgets/connectors/routers properties
function instantsearchWrapper(options) {
    return instantsearch(options);
}
Object.keys(instantsearch).forEach(function (key) {
    try { instantsearchWrapper[key] = instantsearch[key]; } catch (e) { /* skip trapped getters */ }
});
instantsearchWrapper.widgets = {
    clearRefinements, configure, currentRefinements, hits, infiniteHits,
    menu, pagination, rangeInput, rangeSlider, refinementList,
    searchBox, sortBy, stats, panel, hierarchicalMenu, toggleRefinement
};
instantsearchWrapper.connectors = {
    connectCurrentRefinements, connectHits, connectRange, connectStats
};
instantsearchWrapper.routers = { history: historyRouter };

window.algoliasearch = algoliasearchModule.liteClient;
window.instantsearch = instantsearchWrapper;

// Charts
import Chart from 'chart.js/auto';
import Chartkick from 'chartkick';
window.Chart = Chart;
window.Chartkick = Chartkick;
Chartkick.addAdapter(Chart);

// Utilities
import * as momentModule from 'moment';
import _ from 'underscore';
import URI from 'urijs';
window.moment = momentModule.default || momentModule;
window._ = _;
window.URI = URI;

// UI Components
import 'selectize';

// Charts — Highcharts with exporting modules
// MUST use require() because Highcharts UMD sets module.exports directly,
// and ESM default import (import_highcharts.default) resolves to undefined.
// NOTE: In CJS mode, Highcharts exporting modules auto-apply themselves
// during require() — the UMD factory runs with window._Highcharts. Do NOT
// call the result as a function or it will crash.
var Highcharts = require('highcharts');
require('highcharts/modules/exporting');
require('highcharts/modules/offline-exporting');
require('highcharts/modules/export-data');
Highcharts.setOptions({ lang: { thousandsSep: ',' } });
window.Highcharts = Highcharts;

// Numeral.js — used by project chart modules
import numeral from 'numeral';
window.numeral = numeral;
